import Axios from 'axios';
import { action, observable } from 'mobx';
import { API } from '@/config/api';
import { UnderLyingEnum } from '@/@enums/UnderLyingEnum';
import { ScrollPageInfo } from '@/models/data';
import { getExchangeUrl } from '@/utils/location';
import Cookies from 'js-cookie';
import { Notification } from 'element-react';
import i18n from '@/i18n';
import { getBaseUrlBySymbol, getBaseUrlByUnderlyingType } from '@/utils/futures';
import { mergeFundFlowBillTypes } from '@/config/fundFlowBillTypes';

const WELFARE_COUPON_BALANCE_FIELDS = [
  'couponBalance',
  'experienceBalance',
  'feeDeductBalance',
  'pnlDeductBalance',
] as const;

const mergeCouponBalanceFields = <T extends Record<string, any>>(previous: T, next: Partial<T>): T => {
  const merged = { ...previous, ...next };
  WELFARE_COUPON_BALANCE_FIELDS.forEach(field => {
    if (next[field] === undefined || next[field] === null) {
      merged[field] = previous[field];
    }
  });
  return merged;
};

export interface BalanceDetail {
  coin: string; // 币种
  balanceType?: string; // 账户类型：CONTRACT-合约，COPY-跟单
  isolatedMargin: string; // 逐仓保证金冻结
  crossedMargin: string; // 全仓保证金冻结
  openOrderMarginFrozen: string; // 委托保证金: ∑所有委托保证金
  walletBalance: string; // 钱包余额 = 总划转入金 - 总划转出金 + 总已实现盈亏
  assetBalance?: string; // 资产余额 = 钱包余额 + 体验金
  availableBalance: string; // 真实可用 = 钱包余额 - ∑逐仓仓位保证金 - ∑全仓起始保证金 - ∑委托保证金（不显示在c端）
  bonus: string; // 体验金
  bonusBalance?: string; // 体验金余额
  couponBalance?: string; // 卡券总余额
  experienceBalance?: string; // 体验金余额（卡券口径）
  feeDeductBalance?: string; // 手续费抵扣券余额
  pnlDeductBalance?: string; // 盈亏抵扣券余额
}

export interface CoinBalance extends BalanceDetail {
  positionMargin: string; // 仓位保证金：∑逐仓仓位保证金 + ∑全仓仓位起始保证金,
  availableBalanceDisplay: string; // 可用余额 = max(0，真实可用 + ∑全仓未实现盈亏 + 体验金)
  netBalance: string; // 净资产余额 = max(0，真实可用 - abs(∑全仓仓位未实现亏损))
  floatLoss: string; // 未实现亏损 = ∑该结算货币所有开启全仓浮动亏损之和
  floatProfit: string; // 未实现盈亏 = ∑所有浮动盈亏之和
  equity: string; // 账户权益 = 钱包余额 + 未实现盈亏
}

export interface FundFlowItem {
  id: string;
  createdTime: number;
  type: string;
  symbol: string;
  coin: string;
  amount: string;
}

export interface FundRateItem {
  id: string;
  cast: number;
  createdTime: Date;
  positionSide: PositionSide;
  symbol: string;
  coin: string;
}

export interface CoinConfigItem {
  blockUrl: string;
  chain: string;
  chainId: string;
  cmcCurrencyDetailsLink: string;
  cnName: string;
  coinChainName: string;
  enName: string;
  fullName: string;
  group: string;
  id: string;
  imgUrl: string;
  inConfigTimes: string;
  isConvertOpen: string;
  isPayIn: string;
  isPayOut: string;
  isTransfer: string;
  minPayIn: string;
  name: string;
  needMemo: string;
  outConfigTimes: string;
  outDecimal: string;
  sort: string;
  tradeUrl: string;
}

export interface BonusItem {
  [key: string]: any;
}

export default class Asset {
  @observable balance: BalanceDetail;

  @observable displayCoinBalance: BalanceDetail;

  @observable balanceList: BalanceDetail[];

  @observable fundFlowList: FundFlowItem[];

  @observable fundFlowListPageInfo: ScrollPageInfo;

  @observable fundRateList: FundRateItem[];

  @observable fundRateListPageInfo: ScrollPageInfo;

  @observable isFetchingFundFlowList: boolean;

  @observable isFetchingFundRateList: boolean;

  @observable isFetchingTransfer: boolean;

  @observable userFundMap: { [key: number]: any };

  @observable leverFundList: any[];

  @observable fundFlowListForPosition: FundFlowItem[];

  @observable fundFlowListPageInfoForPosition: ScrollPageInfo;

  // @observable leverMarketList: any[];

  @observable supportCoinList: any[];

  @observable spotBalanceList: any[];

  @observable tigerBalanceList: any[];

  @observable otcBalanceList: any[];

  @observable financialBalanceList: any[];

  @observable isFetchingBonusList: boolean;

  @observable bonusList: BonusItem[];

  @observable bonusListPageInfo: ScrollPageInfo;

  @observable foundTypeArr: any[];

  constructor() {
    this.foundTypeArr = [];
    this.balance = {
      coin: '',
      isolatedMargin: '0',
      crossedMargin: '0',
      openOrderMarginFrozen: '0',
      walletBalance: '0',
      assetBalance: '0',
      availableBalance: '0',
      bonus: '0',
    };

    this.displayCoinBalance = {
      coin: '',
      isolatedMargin: '0',
      crossedMargin: '0',
      openOrderMarginFrozen: '0',
      walletBalance: '0',
      availableBalance: '0',
      bonus: '0',
    };

    this.balanceList = [];

    this.fundFlowList = [];
    this.fundFlowListPageInfo = {
      hasPrev: false,
      hasNext: false,
    };

    this.fundRateList = [];
    this.fundRateListPageInfo = {
      hasPrev: false,
      hasNext: false,
    };

    this.isFetchingFundFlowList = false;
    this.isFetchingFundRateList = false;
    this.isFetchingTransfer = false;

    this.fundFlowListForPosition = [];
    this.fundFlowListPageInfoForPosition = {
      hasPrev: false,
      hasNext: false,
    };

    this.userFundMap = {};
    this.leverFundList = [];
    // this.leverMarketList = [];
    this.supportCoinList = [];
    this.spotBalanceList = [];
    this.tigerBalanceList = [];
    this.otcBalanceList = [];
    this.financialBalanceList = [];
    this.isFetchingBonusList = false;
    this.bonusList = [];
    this.bonusListPageInfo = {
      hasPrev: false,
      hasNext: false,
    };
  }

  @action.bound
  setFoundTypeArr(data: any) {
    this.foundTypeArr = data;
  }

  @action.bound
  setLoadingState(key: string, data: boolean) {
    // @ts-ignore
    this[key] = data;
  }

  @action.bound
  setBalanceDetail(data: BalanceDetail) {
    this.balance = data;
  }

  @action.bound
  async fetchBalanceDetail(params: { coin: string; underlyingType: UnderLyingEnum }) {
    const result: BalanceDetail = await Axios.get(API.BALANCE_DETAIL, {
      params: {
        coin: params.coin,
        underlyingType: params.underlyingType,
      },
    });

    if (result) {
      this.setBalanceDetail(result);
    }
  }

  @action.bound
  updateBalanceDetail(data: BalanceDetail) {
    const coin = data.coin;
    const balanceType = data.balanceType || 'CONTRACT';
    const isContractBalance = !data.balanceType || data.balanceType === 'CONTRACT';
    const isCopyBalance = data.balanceType === 'COPY';

    if (!isContractBalance && !isCopyBalance) {
      return;
    }

    if (isContractBalance && this.balance.coin === coin) {
      const newData = mergeCouponBalanceFields(this.balance, data);
      this.setBalanceDetail(newData);
    }

    if (isContractBalance && this.displayCoinBalance.coin === coin) {
      const newData = mergeCouponBalanceFields(this.displayCoinBalance, data);
      this.setDisplayCoinBalance(newData);
    }

    const newList: BalanceDetail[] = [...this.balanceList];
    const index = newList.findIndex(item => item.coin === coin && (item.balanceType || 'CONTRACT') === balanceType);

    if (index > -1) {
      newList[index] = mergeCouponBalanceFields(newList[index], data);
    } else {
      newList.unshift(data);
    }

    this.setBalanceList(newList);
  }

  @action.bound
  setBalanceList(data: BalanceDetail[]) {
    this.balanceList = data;
  }

  @action.bound
  async fetchBalanceList() {
    const result: BalanceDetail[] = await Axios.get(API.BALANCE_LIST);

    if (result) {
      this.setBalanceList(result);
    }
  }

  @action.bound
  getBalanceByCoin(coin: string): BalanceDetail | undefined {
    return this.balanceList.find((item: BalanceDetail) => item.coin === coin && (item.balanceType || 'CONTRACT') === 'CONTRACT');
  }

  // 切换资产区域币种
  @action.bound
  setDisplayCoinBalance(data: BalanceDetail) {
    this.displayCoinBalance = data;
  }

  @action.bound
  async fetchDisplayCoinBalance(params: { coin: string; underlyingType: UnderLyingEnum }) {
    const result: BalanceDetail = await Axios.get(API.BALANCE_DETAIL, {
      params: {
        coin: params.coin,
        underlyingType: params.underlyingType,
      },
      baseURL: '/f',
    });

    if (result) {
      this.setDisplayCoinBalance(result);
    }
  }

  // 资金流水

  @action.bound
  setFundFlowList(data: FundFlowItem[]) {
    this.fundFlowList = data;
  }

  @action.bound
  setFundFlowListPageInfo(data: ScrollPageInfo) {
    const origin = { ...this.fundFlowListPageInfo };
    this.fundFlowListPageInfo = { ...origin, ...data };
  }

  @action.bound
  async fetchFundFlowList({
    coin,
    direction,
    limit = 20,
    type,
    id,
    startTime,
    endTime,
  }: {
    coin?: string;
    direction?: ScrollPaginationDirection;
    limit?: number;
    type?: string;
    id?: string;
    startTime?: number | string;
    endTime?: number | string;
  }) {
    this.setLoadingState('isFetchingFundFlowList', true);

    const result: any = await Axios.get(API.BALANCE_BILLS, {
      params: {
        coin,
        direction,
        limit,
        type,
        id,
        startTime,
        endTime,
      },
    });

    if (result) {
      const { hasPrev, hasNext, items } = result;

      this.setFundFlowList(items || []);
      this.setFundFlowListPageInfo({ hasPrev, hasNext });
    }

    this.setLoadingState('isFetchingFundFlowList', false);
  }

  // 资金费率

  @action.bound
  setFundRateList(data: FundRateItem[]) {
    this.fundRateList = data;
  }

  @action.bound
  setFundRateListPageInfo(data: ScrollPageInfo) {
    const origin = { ...this.fundRateListPageInfo };
    this.fundRateListPageInfo = { ...origin, ...data };
  }

  @action.bound
  async fetchFundRateList({
    symbol,
    direction,
    limit = 20,
    id,
    startTime,
    endTime,
    underlyingType,
  }: {
    symbol?: string;
    direction?: ScrollPaginationDirection;
    limit?: number;
    id?: string;
    startTime?: number | string;
    endTime?: number | string;
    underlyingType?: UnderLyingEnum;
  }) {
    this.setLoadingState('isFetchingFundRateList', true);

    const config: any = {
      params: {
        symbol,
        direction,
        limit,
        id,
        startTime,
        endTime,
      },
    };

    if (symbol) {
      config.baseURL = getBaseUrlBySymbol(symbol);
    } else if (underlyingType) {
      config.baseURL = getBaseUrlByUnderlyingType(underlyingType);
    }

    const result: any = await Axios.get(API.BALANCE_FUNDING_RATE, config);

    if (result) {
      const { hasPrev, hasNext, items } = result;

      this.setFundRateList(items || []);
      this.setFundRateListPageInfo({ hasPrev, hasNext });
    }

    this.setLoadingState('isFetchingFundRateList', false);
  }

  @action.bound
  setUserFundMap = (account: number, data: any) => {
    this.userFundMap = { ...this.userFundMap, [account]: data };
  };

  @action.bound
  async fetchUserFund(account: string) {
    const params = { type: account };
    const result: any = await Axios.get(getExchangeUrl().balance, {
      params,
    });

    if (result) {
      this.spotBalanceList = result.spotBalance;
      this.tigerBalanceList = result.tigerBalance;
      this.otcBalanceList = result.otcBalance;
      this.financialBalanceList = result.financialBalance;
    }
  }

  @action.bound
  setLeverFundList(data: any) {
    this.leverFundList = data;
  }

  @action.bound
  async fetchUserLeverFund({ account }: { account: number }) {
    const result: any = await Axios.post(
      getExchangeUrl().getUserLeverFund,
      {
        account,
      },
      {
        headers: {
          device: 'web',
        },
      },
    );

    if (result) {
      this.setLeverFundList(result.items || []);
    }
  }

  // @action.bound
  // setLeverMarketList(data: any) {
  //   this.leverMarketList = data;
  // }

  @action.bound
  setSupporCoinList(data: any) {
    this.supportCoinList = data;
  }

  // @action.bound
  // async fetchLeverMarketList() {
  //   const result = await Axios.get(getExchangeUrl().getLeverMarketConfig, {
  //     headers: {
  //       device: 'web',
  //     },
  //   });

  //   if (result) {
  //     this.setLeverMarketList(result);
  //   }
  // }

  @action.bound
  async fetchSupportCoinList() {
    const params = { fromWalletType: 'SPOT', toWalletType: 'CONTRACT' };
    const result = await Axios.get(getExchangeUrl().supportCoin, { params });

    if (result) {
      const coinlist = [];
      result.forEach(element => {
        coinlist.push(element.coin);
      });
      this.setSupporCoinList(coinlist);
    }
  }

  @action.bound
  async fetchTransfer(params: { fromWalletType: string; toWalletType: string; coin: string; amount: number }) {
    this.setLoadingState('isFetchingTransfer', true);
    console.log('transfer--balance', params);

    const instance = Axios.create({
      timeout: 10000,
    });

    await instance
      .post(getExchangeUrl().transferBalance, JSON.stringify(params), {
        headers: {
          'Content-Type': 'application/json;charset=UTF-8',
        },
      })
      .then(res => {
        if (res.data.code === -1) {
          Notification.error({
            message: res.data.msg,
            offset: 60,
            duration: 2000,
          });
          return;

        }
        Notification.success({
          message: i18n.t('success.transfer'),
          offset: 60,
          duration: 2000,
        });


        // eslint-disable-next-line consistent-return
        return Promise.resolve();
      })
      .catch(() => {
        Notification.error({
          message: i18n.t('failure.transfer'),
          offset: 60,
          duration: 2000,
        });

      })
      .finally(() => {
        this.setLoadingState('isFetchingTransfer', false);
      });
  }

  // 持仓板块 - 资金流水

  @action.bound
  setFundFlowListForPosition(data: FundFlowItem[]) {
    this.fundFlowListForPosition = data;
  }

  @action.bound
  setFundFlowListPageInfoForPosition(data: ScrollPageInfo) {
    const origin = { ...this.fundFlowListPageInfoForPosition };
    this.fundFlowListPageInfoForPosition = { ...origin, ...data };
  }

  @action.bound
  async fetchFundFlowListForPosition({
    coin,
    symbol,
    direction,
    limit = 10,
    type,
    id,
    balanceType,
    startTime,
    endTime,
  }: {
    coin?: string;
    symbol?: string;
    direction: ScrollPaginationDirection;
    limit?: number;
    type?: string;
    id?: string;
    startTime?: number;
    endTime?: number;
    balanceType?: string;
  }) {
    this.setLoadingState('isFetchingFundFlowList', true);
    const result: any = await Axios.get(API.BALANCE_BILLS, {
      params: {
        coin,
        symbol,
        direction,
        balanceType,
        limit,
        type,
        id,
        startTime,
        endTime,
      },
      baseURL: '/f',
    });

    if (result) {
      const { hasPrev, hasNext, items } = result;

      this.setFundFlowListForPosition(items || []);
      this.setFundFlowListPageInfoForPosition({ hasPrev, hasNext });
    }

    this.setLoadingState('isFetchingFundFlowList', false);
  }

  // 体验金
  @action.bound
  setBonusList(data: BonusItem[]) {
    this.bonusList = data;
  }

  @action.bound
  setBonusListPageInfo(data: ScrollPageInfo) {
    const origin = { ...this.bonusListPageInfo };
    this.bonusListPageInfo = { ...origin, ...data };
  }

  @action.bound
  async fetchBonusList({
    direction,
    limit = 20,
    id,
    startTime,
    endTime,
  }: {
    direction: ScrollPaginationDirection;
    limit?: number;
    id?: string;
    startTime?: number;
    endTime?: number;
  }) {
    this.setLoadingState('isFetchingBonusList', true);

    const result: any = await Axios.get(API.BALANCE_BILLS, {
      params: {
        direction,
        limit,
        id,
        startTime,
        endTime,
      },
    });

    if (result) {
      const { hasPrev, hasNext, items } = result;

      this.setBonusList(items || []);
      this.setBonusListPageInfo({ hasPrev, hasNext });
    }

    this.setLoadingState('isFetchingBonusList', false);
  }

  @action.bound
  async fetchFoundTypeArr() {
    const result: any = await Axios.get(API.billsConf);
    if (result) {
      this.setFoundTypeArr(mergeFundFlowBillTypes(Array.isArray(result) ? result : []));
    }
  }
}
